/* eslint-disable @typescript-eslint/naming-convention */

import {AlgoStatus} from './algo';
import {type Direction, type OrderRecord} from './order';

// eslint-disable-next-line @typescript-eslint/no-restricted-types -- API schemas explicitly distinguish JSON null from omitted fields.
type NullValue = null;
// eslint-disable-next-line @typescript-eslint/no-restricted-types -- Wire strategy legs remain opaque until normalized at an API boundary.
type NonNullObject = object;

export enum SecurityTypes {
  Equity = 'Equity',
  Future = 'Future',
  Option = 'Option',
  FuturesSpread = 'Futures Spread',
}

export type SecurityType = typeof SecurityTypes.Equity | typeof SecurityTypes.Future | typeof SecurityTypes.Option | typeof SecurityTypes.FuturesSpread;

export enum CallPut {
  Call = 'C',
  Put = 'P',
}

/** Schema-facing product shape kept separate from the stable application Symbol model. */
export type WireProductSymbol = {
  callPut?: string | number | NullValue;
  contractSize?: string | number | NullValue;
  contractUnits?: string | NullValue;
  contractYYYYMM?: string | NullValue;
  currency?: string | NullValue;
  decimalPlaces?: number | NullValue;
  description?: string | NullValue;
  exchangeSymbol?: string | NullValue;
  expiry?: string | Date | NullValue;
  firstNoticeDate?: string | Date | NullValue;
  legs?: NonNullObject[] | NullValue;
  name?: string | NullValue;
  normalizedExchangeSymbol?: string | NullValue;
  normalizedSymbol?: string | NullValue;
  normalizedSymbolWithMic?: string | NullValue;
  rootSymbol?: string | NullValue;
  secType?: string | NullValue;
  segmentMic: string;
  strikePrice?: string | number | NullValue;
  symbol: string;
  tickSize?: string | number | NullValue;
  underlyingRootSymbol?: string | NullValue;
};

export type Symbol = {
  symbol?: string;
  segmentMic: string;
  rootSymbol?: string;
  exchangeSymbol?: string;
  expiry?: string;
  contractSize?: number;
  contractUnits?: string;
  currency?: string;
  secType?: SecurityType;
  name?: string;
  normalizedSymbol?: string;
  normalizedExchangeSymbol?: string;
  normalizedSymbolWithMic?: string;
  isTradeble?: boolean;
  firstNoticeDate?: Date;
  exhangeSymbol?: string;// @deprecated
  contractYYYYMM?: string;
  legs?: OrderRecord[];
  tickSize?: number;
  decimalPlaces?: number;
  strikePrice?: number;
  callPut?: CallPut;
  underlyingRootSymbol?: string;
  key?: string;
  description?: string;
};

export type SpreadSymbol = {
  id?: string;
  legs: OrderRecord[];
  defaultDirection?: Direction;
  legsRoots?: OrderRecord[];
} & Symbol;

export enum Result {
  Success = 0,
}

export type MarketDataEvent = {
  symbol: ExchangeSymbolIdentity;
  timestamp_utc: Date;
  feed_timestamp_utc: Date;
  event: SecurityCloseEvent | SecurityOpenEvent | SecurityResetEvent | SecurityLimitEvent | Level1UpdateEvent | SecurityCreatedEvent;
};

export type FuturesStructureChangedEvent = {
  new_front_month: ExchangeSymbolIdentity;
};

export type SecurityCloseEvent = {
  closing_bid: number;
  closing_ask: number;
  close: number;
  close_time_utc: Date;
  settlement_info: SettlementInfoOld;
};
export type SecurityOpenEvent = {
  opening: number;
};
export type SecurityResetEvent = {
  snapshot: Level1SnapshotJSON;
};
export type SecurityLimitEvent = {
  halted: boolean;
  limit_up_price: number;
  limit_down_price: number;
};
export type Level1UpdateEvent = {
  quote: Quote;
  last_trade: LastTradeOld;
};
export type SecurityCreatedEvent = {
  root: ExchangeRootIdentity;
  type: FuturesContract;
};

// you can have multiple instruments
export type SettlementInfoOld = {
  settlement: number;
  settlement_time_utc: Date;
  settlement_size: number;
  settlement_status: SettlementStatus;
};

export type ExchangeSymbolIdentity = {
  symbol: string;
  segmentMic: string;
};

export type ExchangeRootIdentity = {
  root: string;
  segmentMic: string;
};

export type Instrument = {
  symbol: string;
  pricing?: Pricing;
  description?: string;
  refsymbol?: string;
  instrument: string;
};

export type BasicContract = {
  symbol: ExchangeSymbolIdentity;
  maturity: Date;
  settle: Date;
  delivery_date: Date;
  last_trading_date: Date;
};
export type FuturesContract = {
  contract: BasicContract;
};

export type FuturesStateOfTheWorld = {
  result: Result;
};

export type FutureSnapshotJSON = {
  is_front_month?: boolean;
  symbol: ExchangeSymbolIdentity;
  snapshot?: Level1SnapshotJSON;
};

export type Level1SnapshotJSON = {
  high?: number;
  low?: number;
  open?: number;
  close?: number;
  last?: LastTradeOld;
  bid?: Quote;
  ask?: Quote;
  daily_settlement?: SettlementInfoOld;
  previous_daily_settlement?: SettlementInfoOld;
  daily_closing_bid?: Quote;
  daily_closing_ask?: Quote;
  limit_high_price?: number;
  limit_low_price?: number;
  date_of_last_trade?: Date;
  open_interest?: number;
  open_interest_date?: Date;
  previous_net_change?: number;
  previous_percent_change?: number;

  /* quote?: Quote,

    previous_settlement?: number,
    previous_cumulative_price?: number
    previous_bid?: number
    previous_ask?: number
    previous_quote_date?: Date
    previous_close_date?: Date
    previous_settlement_date?: Date
    previous_cumulative_value?: number
    previous_cumulative_volume?: number
    previous_cumulative_volume_date?: Date
    closing_bid?: number
    closing_ask?: number
    closing_quote_date?: Date
    close_status?: CloseStatus
    close_date?: Date
    close_cumulative_volume?: number
    close_cumulative_volume_status?: number
    close_cumulative_volume_date?: Date
    close_cumulative_value?: number
    close_cumulative_value_status?: number
    settlement?: number
    settlement_size?: number
    SettlementStatus?: number
    settlement_time_utc?: Date
    previous_trading_date?: Date
    previous_close?: number
    previous_open?: number
    previous_trade_high?: number
    previous_trade_low?: number
    previous_net_change?: number
    previous_percent_change?: number
    previous_open_interest?: number
    previous_open_interest_date?: Date

    trade_date?: Date

    tick_size?: number */
};
export type LastTradeOld = {
  price: Price;
  volume: number;
};
export type Quote = {
  size: number;
  price: number;
  time_utc?: Date;
};
export type Price = {
  size: number;
  price: number;
  time_utc: Date;
};

enum CloseStatus {
  CLOSESTATUS_NOT_SET = 0,
  CLOSESTATUS_UNKNOWN = 1,
}

enum SettlementStatus {
  SETTLEMENTSTATUS_NOT_SET = 0,
  SETTLEMENTSTATUS_UNKNOWN = 1,
  SETTLEMENTSTATUS_OFFICAL = 2,
}

export type Future = {
  settlementDate: string;
} & Instrument;

export type OrderPrice = {
  price: number;
  priceType: string;
  orderCost: number;
};

export type Pricing = {
  lastPrice: number;
  changePrice: number;
  changePercentage: string;
  bidPrice: {
    currentPrice: number;
    lastPrice: number;
  };
  bidSize: number;
  askPrice: {
    currentPrice: number;
    lastPrice: number;
  };
  askSize: number;
};

/**
 * Option Chain
 */
export type OptionContract = {
  optionType: string;
  strike: number;
  bid: number;
  bidQTY: number;
  ask: number;
  askQTY: number;
  netChange: number;
  lastTradedPrice: number;
  iv: number;
  volumn: number;
};

export type Strike = {
  strikePrice: number;
  call: OptionContract;
  put: OptionContract;
};

export type OptionChain = {
  product: string;
  strikes: Strike[];

};
export type FuturesSpecification = {
  ref_symbol: RefSymbol;
  symbol: ExchangeSymbolIdentity;
  root_symbol: ExchangeRootIdentity;

};
export type RefSymbol = {
  value: string;
};

export type FutureSubscriptionEvent = {
  response: FuturesStateOfTheWorld | MarketDataEvent | FuturesStructureChangedEvent;
};

export enum OrderSide {
  ORDERSIDE_NOTSET = 0,
  ORDERSIDE_BUY = 1,
  ORDERSIDE_SELL = 2,
}
export enum OrderType {
  ORDERTYPE_NOTSET = 0,
  ORDERTYPE_MARKET = 1,
  ORDERTYPE_LIMIT = 2,
  ORDERTYPE_STOP = 3,
  ORDERTYPE_STOP_LIMIT = 4,
}

export enum TimeInForce {
  TIMEINFORCE_REGULAR_TRADING_HOURS = 0,
  TIMEINFORCE_UNTIL_CANCELED = 1,
  TIMEINFORCE_GOOD_TILL_DATE = 6,
}

export enum OrderState {
  ORDERSTATE_NEW = 0,
  ORDERSTATE_PARTIAL_FILLED = 1,
  ORDERSTATE_FILLED = 2,
  ORDERSTATE_DONE_FOR_DAY = 3,
  ORDERSTATE_CANCELED = 4,
  ORDERSTATE_PENDING_CANCEL = 6,
  ORDERSTATE_STOPPED = 7,
  ORDERSTATE_REJECTED = 8,
  ORDERSTATE_SUSPENDED = 9,
  ORDERSTATE_ACKNOWLEDGED = '/',
  ORDERSTATE_PENDING_NEW = 'A',
  ORDERSTATE_CALCULATED = 'B',
  ORDERSTATE_EXPIRED = 'C',
  ORDERSTATE_ACCEPTED_FOR_BIDDING = 'D',
  ORDERSTATE_PENDING_REPLACE = 'E',
  ORDERSTATE_CANCEL_REJECTED = 'R',
  ORDERSTATE_INIT = '.',
  ORDERSTATE_PENDING_INTERNAL_CROSS = '-',
  ORDERSTATE_PENDING_CANCEL_FOR_INTERNAL_CROSS = '+',
  ORDERSTATE_PAUSED = 'P',
}

export const CancelableStatuses = [
  OrderState.ORDERSTATE_NEW,
  OrderState.ORDERSTATE_PARTIAL_FILLED,
  OrderState.ORDERSTATE_ACKNOWLEDGED,
  OrderState.ORDERSTATE_PENDING_NEW,
  OrderState.ORDERSTATE_CALCULATED,
];

export const ResumableStatuses = [
  OrderState.ORDERSTATE_PAUSED,
];

export const CancelledStatuses = [
  OrderState.ORDERSTATE_CANCELED,
  OrderState.ORDERSTATE_DONE_FOR_DAY,
  OrderState.ORDERSTATE_EXPIRED,
  OrderState.ORDERSTATE_REJECTED,
  OrderState.ORDERSTATE_STOPPED,
];

export const AbortedStatues = [
  AlgoStatus.ALGOSTATUS_ABORTED,
  AlgoStatus.ALGOSTATUS_ABORTED_BY_SYSTEM,
  AlgoStatus.ALGOSTATUS_ERROR,
  AlgoStatus.ALGOSTATUS_INCOMPLETE_REACHED_END_TIME,
  AlgoStatus.ALGOSTATUS_INVALID,
];

export const AbortableStatuses = [
  AlgoStatus.ALGOSTATUS_INCOMPLETE_MARKET_CLOSED,
  AlgoStatus.ALGOSTATUS_INITIALIZING,
  AlgoStatus.ALGOSTATUS_LAUNCH_REQUESTED,
  AlgoStatus.ALGOSTATUS_LAUNCH_REQUESTED,
  AlgoStatus.ALGOSTATUS_PAUSED,
  AlgoStatus.ALGOSTATUS_PAUSE_REQUESTED,
  AlgoStatus.ALGOSTATUS_RESUME_REQUESTED,
  AlgoStatus.ALGOSTATUS_RUNNING,
  AlgoStatus.ALGOSTATUS_VALIDATING,
];

export enum ErrorLevelCode {
  LOW = 0,
  MEDIUM = 1,
  HIGH = 2,

}
export type Error = {
  level: ErrorLevelCode;
  description: string;
  label: string;
};
export type Currency = Record<string, unknown>;
export type InstrumentType = Record<string, unknown>;
export type ExecutionAlgorithm = Record<string, unknown>;

export type InstrumentLookupResponseJSON = {
  hitsList: InstrumentLookupHitJSON[];

};
export type InstrumentLookupHitJSON = {
  ref_symbol: RefSymbol;
  symbol: ExchangeSymbolIdentity;
  instrument: InstrumentType;
};
export type PositionAndGreeks = {
  account: string;
  ref_symbol: RefSymbol;
  symbol: ExchangeSymbolIdentity;
  unit_of_measure: string;
  quantity: number;
  pnl: number;
  trade_quantity: number;
  delta_shares: number;// number of units to trade to get to delta neutral
  delta: number;
  gamma: number;
  vega: number;
  theta: number;

};

export type Account = {
  id: number;
  code: string;
  desciption: string;
  isActive: boolean;
  accountOwner: string;
  systemCode: string;
  defaultClearingAccountCode: string;
  neoVestAccountCode: string;
  businessUnit: string;
  businessCategory: string;
  processingFirm: string;
  marginAccountType: string;
  reportingUnit: string;
  accountGroup: string;
  clearingString: string;
  isGdp: boolean;
};

export type ContractData = {
  qep_symbol?: string;
  segment_mic?: string;
  operating_mic?: string;
  qep_product_code?: string;
  trading_group?: string;
  exch_symbol?: string;
  description?: string;
  sec_type?: string;
  category?: string | NullValue;
  underlying_qep_product_code?: string | NullValue;
  underlying_qep_symbol?: string | NullValue;
  strategy_type?: string | NullValue;
  exch_sec_type?: string | NullValue;
  exch_strategy_type?: string | NullValue;
  exch_strike_multiplier?: string | number | NullValue;
  bloomberg_ticker?: string | NullValue;
  isin?: string | NullValue;
  figi?: string | NullValue;
  cfi_code?: string | NullValue;
  currency_iso_code?: string;
  expiry_code?: string | number | NullValue;
  expiry_week?: string | number | NullValue;
  contract_year?: string | number | NullValue;
  contract_month?: string | number | NullValue;
  expiration_date_local?: string;
  last_trading_time_local?: string | NullValue;
  first_notice_date_local?: string | NullValue;
  strike_price?: string | number | NullValue;
  call_put?: string | NullValue;
  exercise_type?: string | NullValue;
  contract_size?: string | number | NullValue;
  contract_units?: string;
  tick_size_str?: string | NullValue;
  tick_size_type?: string | NullValue;
  tick_size?: string | number | NullValue;
  tick_value?: string | number | NullValue;
  fractional_price_format?: string | NullValue;
  order_exec_algo?: string | NullValue;
  implied_pricing?: string | number | NullValue;
  exch_price_multiplier?: string | number | NullValue;
  settlement_method?: string | NullValue;
  database_first_date_utc?: string | NullValue;
  created_time_utc?: string | NullValue;
  updated_time_utc?: string | NullValue;
};

export enum StaticRequestErrorMessages {
  REQUEST_TIMEOUT = 'Request timed out',
}
